/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 📱 TABLE REGISTRATION PAGE
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 
 * PUBLIC Route (kein Login erforderlich!)
 * 
 * FLOW:
 * 1. User öffnet /table-registration auf Tablet
 * 2. Gibt Tisch-Name ein (z.B. "Tisch 1")
 * 3. Optional: Club-Code oder Club-Auswahl
 * 4. System erstellt anonymous Supabase User
 * 5. table_device Eintrag wird erstellt (status: pending)
 * 6. device_uuid wird in localStorage gespeichert
 * 7. Redirect zu /table-view (wartet dort auf Approval)
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  registerTableDevice,
  getDeviceInfo,
  getTableDeviceByUuid,
} from '../services/tableDevices';
import { supabase } from '../lib/supabase';
import './TableRegistration.css';

const DEVICE_UUID_KEY = 'snooker_table_device_uuid';

export const TableRegistration: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tableName, setTableName] = useState('');
  const [location, setLocation] = useState('');
  const [clubCode, setClubCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingExisting, setCheckingExisting] = useState(true);

  // Check if this device is already registered
  useEffect(() => {
    const checkExistingDevice = async () => {
      const existingUuid = localStorage.getItem(DEVICE_UUID_KEY);
      if (!existingUuid) {
        setCheckingExisting(false);
        return;
      }

      try {
        const device = await getTableDeviceByUuid(existingUuid);
        if (device) {
          // Device exists, redirect to table view
          navigate('/table-view');
        } else {
          // UUID exists but device was deleted, clear it
          localStorage.removeItem(DEVICE_UUID_KEY);
          setCheckingExisting(false);
        }
      } catch (err) {
        console.error('Error checking existing device:', err);
        localStorage.removeItem(DEVICE_UUID_KEY);
        setCheckingExisting(false);
      }
    };

    checkExistingDevice();
  }, [navigate]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!tableName.trim()) {
      setError(t('tableDevices.registration.tableNameRequired'));
      return;
    }

    setLoading(true);

    try {
      // Club-Code Lookup (falls angegeben)
      let clubId: number | undefined;
      
      if (clubCode.trim()) {
        // Suche Club anhand des 6-stelligen Codes
        const { data: clubs, error: clubError } = await supabase
          .from('clubs')
          .select('id, name')
          .eq('code', clubCode.trim().toUpperCase())
          .single();

        if (clubError || !clubs) {
          setError(t('tableDevices.registration.clubNotFound') || 'Club not found');
          setLoading(false);
          return;
        }

        clubId = clubs.id;
      }

      const deviceInfo = getDeviceInfo();

      const { device, deviceUuid } = await registerTableDevice({
        table_name: tableName.trim(),
        club_id: clubId,
        location: location.trim() || undefined,
        device_info: deviceInfo,
      });

      // Save device UUID to localStorage
      localStorage.setItem(DEVICE_UUID_KEY, deviceUuid);

      // Redirect to table view
      navigate('/table-view');
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || t('tableDevices.registration.error'));
    } finally {
      setLoading(false);
    }
  };

  if (checkingExisting) {
    return (
      <div className="table-registration-container">
        <div className="checking-card">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-registration-container">
      <div className="registration-card">
        <div className="registration-header">
          <h1>🏓 {t('tableDevices.registration.title')}</h1>
          <p className="subtitle">{t('tableDevices.registration.subtitle')}</p>
        </div>

        <form onSubmit={handleRegister} className="registration-form">
          {/* Table Name */}
          <div className="form-group">
            <label htmlFor="tableName">
              {t('tableDevices.registration.tableName')} *
            </label>
            <input
              id="tableName"
              type="text"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              placeholder={t('tableDevices.registration.tableNamePlaceholder')}
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Location (Optional) */}
          <div className="form-group">
            <label htmlFor="location">
              {t('tableDevices.registration.location')}
            </label>
            <input
              id="location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t('tableDevices.registration.locationPlaceholder')}
              disabled={loading}
            />
          </div>

          {/* Club Code (Optional - TODO: Implement lookup) */}
          <div className="form-group">
            <label htmlFor="clubCode">
              {t('tableDevices.registration.clubCode')}
            </label>
            <input
              id="clubCode"
              type="text"
              value={clubCode}
              onChange={(e) => setClubCode(e.target.value)}
              placeholder={t('tableDevices.registration.clubCodePlaceholder')}
              disabled={loading}
            />
            <small className="form-hint">
              {t('tableDevices.registration.clubCodeHint')}
            </small>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button type="submit" className="btn-register" disabled={loading}>
            {loading
              ? t('tableDevices.registration.registering')
              : t('tableDevices.registration.register')}
          </button>
        </form>

        {/* Device Info Display */}
        <div className="device-info">
          <p className="info-title">ℹ️ {t('tableDevices.registration.deviceInfo')}</p>
          <p className="info-text">
            {t('tableDevices.registration.screen')}: {window.screen.width} × {window.screen.height}
          </p>
          <p className="info-text">{t('tableDevices.registration.platform')}: {navigator.platform}</p>
        </div>
      </div>
    </div>
  );
};

